"use client";

import { useState, useMemo } from "react";
import Button from "@components/atoms/Button";
import ImageAtom from "@components/atoms/Image";
import { FAQItem } from "@components/molecules/FAQItem";
import assetLoader from "@utils/internalImageLoder";
import {
  HorizontalTab,
  HorizontalTabContent,
  HorizontalTabList,
  HorizontalTabTrigger,
} from "@components/molecules/HorizontalTab";
import styles from "./FAQHero.module.scss";
import {
  MMSisterFAQListResponse,
  FAQItem as FAQItemType,
} from "@interfaces/faq.types";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { getSelectedCityClient } from "@helpers/client/getSelectedCity";

const INITIAL_ITEMS_COUNT = 6;
const LOAD_MORE_INCREMENT = 6;

export default function FAQHero({
  faqList,
  mmName,
  pageType,
  brandName,
  modelName,
  carAvailability,
}: {
  faqList: MMSisterFAQListResponse;
  mmName: string;
  pageType?: string;
  brandName?: string;
  modelName?: string;
  carAvailability?: string;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsCount, setItemsCount] = useState<Record<string, number>>(() => {
    const initialCount: Record<string, number> = {};
    Object.keys(faqList).forEach((key) => {
      initialCount[key] = INITIAL_ITEMS_COUNT;
    });
    return initialCount;
  });
  const { name: cityName } = getSelectedCityClient();

  // Filter FAQ list based on search query
  const filteredFaqList = useMemo(() => {
    if (!searchQuery.trim()) {
      return faqList;
    }

    const searchTerm = searchQuery.toLowerCase();
    const filtered: MMSisterFAQListResponse = {} as MMSisterFAQListResponse;

    Object.keys(faqList).forEach((category) => {
      const categoryFaqs = faqList[
        category as keyof MMSisterFAQListResponse
      ] as FAQItemType[];
      const filteredCategoryFaqs = categoryFaqs.filter((faq) => {
        const question = faq.question?.toLowerCase() || "";
        const answer = faq.answer?.toLowerCase() || "";

        return question.includes(searchTerm) || answer.includes(searchTerm);
      });

      if (filteredCategoryFaqs.length > 0) {
        filtered[category as keyof MMSisterFAQListResponse] =
          filteredCategoryFaqs;
      }
    });

    return filtered;
  }, [faqList, searchQuery]);

  // Calculate total results for search
  const totalResults = useMemo(() => {
    return Object.values(filteredFaqList).reduce((total, category) => {
      return total + category.length;
    }, 0);
  }, [filteredFaqList]);

  const handleLoadMore = (key: string) => {
    setItemsCount((prev) => ({
      ...prev,
      [key]: prev[key] + LOAD_MORE_INCREMENT,
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    // Reset items count when searching
    const newItemsCount: Record<string, number> = {};
    Object.keys(faqList).forEach((key) => {
      newItemsCount[key] = INITIAL_ITEMS_COUNT;
    });
    setItemsCount(newItemsCount);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Search is already handled by the input change
  };

  const handleTabChange = async (tabValue: string) => {
    // Track tab click event
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType || "",
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_filter",
        selected_value: tabValue,
        filter_type: "faq_category",
        car_availability: carAvailability || "",
        vehicle_make: brandName || "",
        vehicle_model: modelName || "",
        city_name: cityName || "",
      },
    });
  };

  const defaultValue = Object.keys(filteredFaqList).shift();

  return (
    <section className={styles["hero"]}>
      <h1>{`Frequently asked questions about ${mmName}`}</h1>
      {/* <form className={styles["hero__search"]} onSubmit={handleSearchSubmit}>
        <Input
          className={styles["hero__search__input"]}
          placeholder="Search questions..."
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <Button type="submit" className={styles["hero__search__btn"]}>
          Search
        </Button>
      </form> */}

      {Object.keys(filteredFaqList).length > 0 ? (
        <HorizontalTab
          defaultValue={defaultValue}
          onValueChange={handleTabChange}
        >
          <HorizontalTabList>
            {Object.keys(filteredFaqList).map((key) => (
              <HorizontalTabTrigger key={key} value={key}>
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </HorizontalTabTrigger>
            ))}
          </HorizontalTabList>

          {Object.keys(filteredFaqList).map((key) => {
            const faqItems =
              filteredFaqList[key as keyof MMSisterFAQListResponse];
            const currentCount = itemsCount[key] || INITIAL_ITEMS_COUNT;
            const hasMoreItems = faqItems.length > currentCount;

            return (
              <HorizontalTabContent key={key} value={key}>
                <ul className={styles["hero__faq-list"]}>
                  {faqItems.slice(0, currentCount).map((faq) => (
                    <li key={faq.question}>
                      <FAQItem faq={faq} />
                    </li>
                  ))}
                  {hasMoreItems && (
                    <li>
                      <Button
                        className={styles["hero__load-more-btn"]}
                        onClick={() => handleLoadMore(key)}
                      >
                        Load more
                        <ImageAtom
                          src={`${assetLoader()}/icons/Chevron_down_dark.svg`}
                          alt="Chevron Down"
                          width={20}
                          height={20}
                        />
                      </Button>
                    </li>
                  )}
                </ul>
              </HorizontalTabContent>
            );
          })}
        </HorizontalTab>
      ) : searchQuery ? (
        <div className={styles["hero__no-results"]}>
          <p>
            No results found for &quot;{searchQuery}&quot;. Try a different
            search term.
          </p>
        </div>
      ) : null}
    </section>
  );
}
